import React, { useCallback, useState } from 'react';
import Icon from '../../../components/AppIcon';

const UploadZone = ({ 
  onFilesSelected, 
  disabled = false,
  maxFiles = 9,
  currentFileCount = 0
}) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const handleFileInput = useCallback((e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      onFilesSelected(files);
    }
    e.target.value = '';
  }, [onFilesSelected]);

  const handleCameraCapture = useCallback((e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      onFilesSelected(files);
    }
    e.target.value = '';
  }, [onFilesSelected]);

  const remainingSlots = maxFiles - currentFileCount;
  const canUpload = remainingSlots > 0 && !disabled;

  return (
    <div className="space-y-3">
      {/* Native Camera Picker */}
      <div className="relative">
        <input
          type="file"
          id="camera-capture"
          className="hidden"
          accept="image/jpeg,image/png,image/heic,image/jpg"
          capture="environment"
          onChange={handleCameraCapture}
          disabled={!canUpload}
        />
        <label
          htmlFor="camera-capture"
          className={`flex items-center justify-between w-full p-5 rounded-2xl border-2 transition-all duration-250 ${
            canUpload
              ? 'border-purple-300 bg-purple-50 hover:bg-purple-100 active:scale-[0.98] cursor-pointer'
              : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-200 flex items-center justify-center">
              <Icon name="Camera" size={24} className="text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                Take Photo
              </h3>
              <p className="text-sm text-gray-600">
                Use your camera
              </p>
            </div>
          </div>
          <Icon name="ChevronRight" size={20} className="text-gray-400" />
        </label>
      </div>

      {/* Native Library Picker */}
      <div className="relative">
        <input
          type="file"
          id="file-upload"
          className="hidden"
          accept="image/jpeg,image/png,image/heic,image/jpg"
          multiple
          onChange={handleFileInput}
          disabled={!canUpload}
        />
        <label
          htmlFor="file-upload"
          className={`flex items-center justify-between w-full p-5 rounded-2xl border-2 transition-all duration-250 ${
            canUpload
              ? 'border-purple-300 bg-purple-50 hover:bg-purple-100 active:scale-[0.98] cursor-pointer'
              : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-200 flex items-center justify-center">
              <Icon name="FolderOpen" size={24} className="text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                Choose from Library
              </h3>
              <p className="text-sm text-gray-600">
                Select multiple photos
              </p>
            </div>
          </div>
          <Icon name="ChevronRight" size={20} className="text-gray-400" />
        </label>
      </div>

      {/* Collapsible Technical Details */}
      <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
        <button
          onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
          aria-expanded={isDetailsExpanded}
          aria-label="Toggle technical details"
        >
          <div className="flex items-center gap-2">
            <Icon name="Info" size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-600">Technical Details</span>
          </div>
          <Icon 
            name={isDetailsExpanded ? "ChevronUp" : "ChevronDown"} 
            size={18} 
            className="text-gray-500 transition-transform duration-200"
          />
        </button>
        
        {isDetailsExpanded && (
          <div className="px-3 pb-3 space-y-2 border-t border-gray-200 pt-3">
            <div className="flex items-center gap-2">
              <Icon name="FileImage" size={14} className="text-purple-600" />
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-900">Supported Formats</p>
                <p className="text-xs text-gray-600">JPG, PNG, HEIC</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="HardDrive" size={14} className="text-purple-600" />
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-900">File Size Limit</p>
                <p className="text-xs text-gray-600">Max 10MB per photo</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Images" size={14} className="text-purple-600" />
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-900">Available Slots</p>
                <p className="text-xs text-gray-600">
                  {remainingSlots} of {maxFiles} remaining
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {!canUpload && remainingSlots === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-red-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 mb-1">Upload Limit Reached</p>
            <p className="text-xs text-red-700">
              Remove some photos or upgrade to add more
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadZone;